import { Field, Formik, useFormikContext } from 'formik';
import { observer } from 'mobx-react';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Button, Form, Modal } from 'react-bootstrap';
import toast from 'react-hot-toast';
import { FormattedMessage } from 'react-intl';
import { ProcessTypeEnum } from '../../../constants/enums';
import { useStore } from '../../../contexts/store-provider';
import {
  getTenantAccessFromLocalStorage,
  getTenantIdFromLocalStorage,
} from '../../../helpers/localstorage-helper';
import { IProcessOptions } from '../../../models/forms/IAddUpdateProcessTemplate';
import { IActionFormatter } from '../../../models/ICommon';
import LoaderButton from '../../../shared-components/button/loader-button';
import FormikFormSelect from '../../../shared-components/FormikFormHandlers/FormikFormSelect';
import FormLoader from '../../../shared-components/FormLoader/FormLoader';
import Label from '../../../shared-components/label/label';
import {
  DownIcon,
  RemoveIcon,
  UpIcon,
} from '../../../shared-components/svgs/svgs';
import { formatMessage } from '../../../translations/format-message';
import ProductStepper from './product-stepper';
import { ViewTypeOptions } from '../../../constants/options.constant';
import {
  normalizeDimensionsByColumn,
  transformDimensionData,
} from '../../../helpers/array-to-tree-helper';
import './step-four.css';
import ProcessTabView from '../components/tab-view/process-tab-view';
import ProcessTabContent from '../components/tab-view/process-tab-content';
import CommonProcessTabView from '../components/common-tab-view/common-process-tab-view';
import CommonProcessTabContent from '../components/common-tab-view/common-process-tab-content';
import { Check } from 'react-bootstrap-icons';

interface IProps {
  id: number;
  initalValues: any;
  isLoading: boolean;
  next: (newData: any, final?: boolean) => void;
  prev: (newData: any) => void;
  handleSaveAndNext: (newData: any, final?: boolean) => void;
  setCloseModal: (isClose: boolean) => void;
  setCurrentStep: (step: number) => void;
}

const DimensionOrderFetcher = () => {
  const { values, setFieldValue } = useFormikContext<any>();
  const { productStore } = useStore();

  useEffect(() => {
    if (values?.Process?.length > 0) {
      const processIds = values.Process.filter((p: any) => p.ProcessId > 0).map(
        (p: any) => p.ProcessId
      );
      if (processIds.length > 0) {
        productStore.GetDimensionByProcessIdService(
          processIds,
          setFieldValue,
          values
        );
      }
    }
  }, []);

  return null;
};

const StepThree = (props: IProps) => {
  const { processStore, teplarPrintStore, processTemplateStore, productStore } =
    useStore();
  const { id } = props;
  const { GetTeplarByProductIdService } = teplarPrintStore;
  const { getDimensionByProcessIdState } = productStore;
  const {
    GetProcessTemplateByIdService,
    productProcessTemplates,
    allAvailableProcessTemplates,
    GetAllTemplatesService,
    resetProcessTemplateDetail,
  } = processTemplateStore;

  const [selectedProcessId, setSelectedProcessId] = useState<number>(-3);

  const normalizedInitialValues = useMemo(() => {
    const baseValues = props.initalValues || {};
    const sourceProcess = Array.isArray(baseValues?.Process)
      ? baseValues.Process
      : [];
    const process = sourceProcess.map((p: any) => ({
      ...p,
      fileIndices: Array.isArray(p?.fileIndices) ? [...p.fileIndices] : [],
    }));

    const commonFromRoot = Array.isArray(baseValues?.CommonProcess)
      ? baseValues.CommonProcess
      : [];
    const commonIndex = process.findIndex(
      (p: any) => Number(p?.ProcessId) === -3
    );
    const commonFromProcess =
      commonIndex >= 0 ? process[commonIndex]?.fileIndices || [] : [];
    const mergedCommon = [...commonFromProcess];

    commonFromRoot.forEach((tab: any) => {
      const heading = String(tab?.FileDetail?.[0]?.Heading || '')
        .trim()
        .toLowerCase();
      const path = String(
        tab?.FileDetail?.[0]?.S3FilePath || tab?.FileDetail?.[0]?.FilePath || ''
      ).trim();
      const exists = mergedCommon.some((existing: any) => {
        const existingHeading = String(existing?.FileDetail?.[0]?.Heading || '')
          .trim()
          .toLowerCase();
        const existingPath = String(
          existing?.FileDetail?.[0]?.S3FilePath ||
            existing?.FileDetail?.[0]?.FilePath ||
            ''
        ).trim();

        // Different common tabs may intentionally reuse the same file.
        // When both tabs have headings, the heading is their UI identity;
        // deduplicating by path here can drop a valid tab (for example C-3
        // when C-1 and C-3 point to the same PDF).
        if (heading && existingHeading) {
          return existingHeading === heading;
        }

        const existingTabIndex = Number(existing?.TabIndex || 0);
        const tabIndex = Number(tab?.TabIndex || 0);
        if (existingTabIndex > 0 && tabIndex > 0) {
          return existingTabIndex === tabIndex;
        }

        return Boolean(path && existingPath === path);
      });
      if (!exists) {
        mergedCommon.push({
          ...tab,
          IsDefault: true,
          IsDimension: false,
        });
      }
    });

    // Cap to the same limit enforced by the UI (commonTabLimit = 5).
    // Prevents a phantom 6th tab from the API response of a copied product
    // from being loaded into the form and distributed across all processes.
    const MAX_COMMON_TABS = 5;
    if (mergedCommon.length > MAX_COMMON_TABS) {
      mergedCommon.splice(MAX_COMMON_TABS);
    }

    if (commonIndex >= 0) {
      process[commonIndex] = {
        ...process[commonIndex],
        ProcessId: -3,
        DueDates: process[commonIndex]?.DueDates ?? 0,
        fileIndices: mergedCommon,
      };
    } else {
      process.push({
        ProcessId: -3,
        DueDates: 0,
        fileIndices: mergedCommon,
      });
    }

    const dimensionIndex = process.findIndex(
      (p: any) => Number(p?.ProcessId) === -4
    );
    if (dimensionIndex < 0) {
      process.push({
        ProcessId: -4,
        DueDates: 0,
        fileIndices: [],
      });
    } else if (!Array.isArray(process[dimensionIndex]?.fileIndices)) {
      process[dimensionIndex] = {
        ...process[dimensionIndex],
        fileIndices: [],
      };
    }

    const firstRealProcess = process.find((p: any) => Number(p?.ProcessId) > 0);
    const existingSelectedProcessForDimension = Number(
      baseValues?.SelectedProcessForDimensionTable
    );
    const selectedProcessForDimensionTable =
      existingSelectedProcessForDimension > 0
        ? existingSelectedProcessForDimension
        : Number(firstRealProcess?.ProcessId || -1);

    const selectedProcessForDimension = process.find(
      (p: any) =>
        Number(p?.ProcessId) === Number(selectedProcessForDimensionTable)
    );

    const rootDimension = baseValues?.Dimension || {};
    const processDimensionDetails =
      selectedProcessForDimension?.DimensionDetails || {};

    const selectedColumns =
      Array.isArray(rootDimension?.SelectedColumns) &&
      rootDimension.SelectedColumns.length > 0
        ? rootDimension.SelectedColumns
        : Array.isArray(processDimensionDetails?.SelectedColumns)
        ? processDimensionDetails.SelectedColumns
        : [];

    const processTableData =
      Array.isArray(rootDimension?.TableData) && rootDimension.TableData.length > 0
        ? rootDimension.TableData
        : Array.isArray(processDimensionDetails?.TableData)
        ? processDimensionDetails.TableData
        : [];
    const tableData =
      processTableData.some((row: any) => row?.DimensionId || row?.DimensionColumn)
        ? transformDimensionData(normalizeDimensionsByColumn(processTableData))
        : processTableData;

    return {
      ...baseValues,
      ProcessTemplate: '-1',
      Process: process,
      CommonProcess: mergedCommon,
      SelectedProcessForDimensionTable: selectedProcessForDimensionTable,
      Dimension: {
        SelectedColumns: selectedColumns,
        TableData: tableData,
      },
      selectedProcesses:
        baseValues.selectedProcesses?.map((p: any) => ({
          ...p,
          StandardTime: p.StandardTime || '00:00',
        })) || [],
    };
  }, [props.initalValues]);

  const dedupeSelectedColumns = (columns: any[] = []) => {
    const seen = new Set<string>();
    const dedupedColumns = columns.filter((col: any) => {
      const id = col?.id ?? col?.value ?? col?.data?.Id ?? '';
      const label = String(col?.label || col?.data?.Messages || '').trim().toLowerCase();
      const key = `${id}__${label}`;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });

    const masterOrder = Array.isArray(productStore.getDimensionTableColumnKeys)
      ? productStore.getDimensionTableColumnKeys
        .map((col: any) => Number(col?.Id || 0))
        .filter((id: number) => id > 0)
      : [];
    const order =
      masterOrder.length > 0 ? masterOrder : productStore.getDimensionOrder || [];
    if (!order || order.length === 0) return dedupedColumns;

    return [...dedupedColumns].sort((a: any, b: any) => {
      const indexA = order.indexOf(Number(a?.id || a?.value || a?.data?.Id || 0));
      const indexB = order.indexOf(Number(b?.id || b?.value || b?.data?.Id || 0));

      if (indexA === -1 && indexB === -1) return 0;
      if (indexA === -1) return 1;
      if (indexB === -1) return -1;
      return indexA - indexB;
    });
  };

  const hasValidFileDetails = (fileDetails: any[] = []) =>
    Array.isArray(fileDetails) &&
    fileDetails.some((fd: any) => {
      const fileId = Number(fd?.FileId || 0);
      return Boolean(
        (fd?.S3FilePath && String(fd.S3FilePath).trim() !== '') ||
          (fd?.FilePath && String(fd.FilePath).trim() !== '') ||
          fileId > 0
      );
    });

  const handleSubmit = (values: any) => {
    const commonTabs = values.Process?.find((p: any) => p.ProcessId === -3)?.fileIndices || [];

    // Block Next if any common tab has an image/file but a blank heading
    const hasBlankHeadingWithFile = commonTabs.some((fi: any) => {
      const heading = String(fi?.FileDetail?.[0]?.Heading || '').trim();
      const hasFile = hasValidFileDetails(fi?.FileDetail || []);
      return hasFile && heading === '';
    });
    if (hasBlankHeadingWithFile) {
      toast.error(formatMessage('please_enter_heading'));
      return;
    }

    const commonHeadings = commonTabs
      .map((fi: any) => String(fi?.FileDetail?.[0]?.Heading || '').trim().toLowerCase())
      .filter(Boolean);
    if (new Set(commonHeadings).size !== commonHeadings.length) {
      toast.error(formatMessage('duplicate_found_tabs'));
      return;
    }
    // console.log('[STEP3 SUBMIT] Incoming values.Process:', values.Process?.map((p: any) => ({
    //   ProcessId: p.ProcessId,
    //   fileIndices: p.fileIndices?.filter((f: any) => f.IsDefault).map((f: any) => ({
    //     TabIndex: f.TabIndex,
    //     FileIds: f.FileDetail?.map((fd: any) => fd.FileId)
    //   }))
    // })));
    const submissionSummary = values.Process.map((p: any) => ({
      ProcessName: p.ProcessName,
      TotalTabs: p.fileIndices?.length || 0,
      CommonTabs: p.fileIndices?.filter((fi: any) => fi.IsDefault || fi.isDefault).map((fi: any) => ({
        Heading: fi.FileDetail?.[0]?.Heading,
        FileId: fi.FileDetail?.[0]?.FileId
      })) || [],
      RegularTabs: p.fileIndices?.filter((fi: any) => !fi.IsDefault && !fi.isDefault && !fi.IsDimension).map((fi: any) => ({
        Heading: fi.FileDetail?.[0]?.Heading,
        FileId: fi.FileDetail?.[0]?.FileId
      })) || []
    }));

    // console.log('--- FINAL SUBMISSION SUMMARY (ADD/UPDATE) ---', submissionSummary);
    // console.log('Final Payload Object:', values);

    // if (loPadStepThree) {
    let tempValues = {
      ...values,
      Process: values.Process?.map((rest) => {
        return {
          ...rest,
          fileIndices:
            rest.ProcessId !== -3 && rest?.fileIndices?.length > 0
              ? rest?.fileIndices
              : (rest?.fileIndices ?? []),
        };
      }),
    };
    let dimensionTable = {
      TabIndex: 1,
      IsDefault: false,
      IsDimension: true,
      FileDetail: [],
      CheckItems: [],
      DimensionsData: [],
      ProductTeplars: [],
      IsDimensionTable: true,
    };

    const commonFromProcess =
      tempValues?.Process?.find((p) => p?.ProcessId === -3)?.fileIndices ?? [];
    const commonFromRoot = tempValues?.CommonProcess ?? [];
    const commonSeed =
      commonFromProcess.length > 0 ? commonFromProcess : commonFromRoot;

    let commonProcess: any[] = [
      ...(commonSeed?.map((e: any) => ({
        ...e,
        IsDefault: true,
        IsDimension: false,
        CheckItems: Array.isArray(e?.CheckItems) ? [...e.CheckItems] : [],
        FileDetail: Array.isArray(e?.FileDetail) ? [...e.FileDetail] : [],
        DimensionsData: Array.isArray(e?.DimensionsData)
          ? [...e.DimensionsData]
          : [],
      })) ?? []),
    ];

    let partialData = {
      ...tempValues,
      Process: tempValues.Process?.map((rest) => {
        let files = [
          ...(rest.fileIndices?.map((e) => ({
            ...e,
          })) ?? []),
        ];
        const usedExIndices = new Set<number>();
        const usedFileIds = new Set<number>();
        const finalIndices =
          rest.ProcessId > 0
            ? [
              ...commonProcess.map((e: any) => {
                  // Find existing tab in this specific process to preserve CheckItems.
                  // Common tabs are user-renamable, so the current heading is their
                  // stable UI identity after C4 -> C3 / C5 -> C4 reorder flows.
                  let matchedIndex = rest?.fileIndices?.findIndex(
                    (ex: any, idx: number) => {
                      if (usedExIndices.has(idx)) return false;
                      if (!ex.IsDefault) return false;
                      const exHeading = String(ex?.FileDetail?.[0]?.Heading || '').trim().toLowerCase();
                      const eHeading = String(e?.FileDetail?.[0]?.Heading || '').trim().toLowerCase();
                      return Boolean(exHeading && eHeading && exHeading === eHeading);
                    }
                  );

                  // Fallback to file identity only when heading did not identify a tab.
                  if (matchedIndex === -1) {
                    matchedIndex = rest?.fileIndices?.findIndex(
                      (ex: any, idx: number) => {
                        if (usedExIndices.has(idx)) return false;
                        if (!ex.IsDefault) return false;
                        return ex?.FileDetail?.some((ef: any) => 
                          e?.FileDetail?.some((cf: any) => {
                            const efId = Number(ef.FileId || 0);
                            const cfId = Number(cf.FileId || 0);
                            if (efId > 0 && cfId > 0 && efId === cfId) return true;

                            const efPath = ef.FilePath || ef.S3FilePath;
                            const cfPath = cf.FilePath || cf.S3FilePath;
                            if (efPath && cfPath && efPath === cfPath) return true;
                            return false;
                          })
                        );
                      }
                    );
                  }

                  // Last fallback: position, but only for tabs without headings.
                  if (matchedIndex === -1) {
                    const commonIdx = commonProcess.indexOf(e);
                    const existingCommonTabs = rest.fileIndices.filter((ex: any) => ex.IsDefault);
                    const matchingExTab = existingCommonTabs[commonIdx];
                    const eHeading = String(e?.FileDetail?.[0]?.Heading || '').trim();
                    const matchingHeading = String(matchingExTab?.FileDetail?.[0]?.Heading || '').trim();
                    if (matchingExTab && !eHeading && !matchingHeading) {
                      const idxInFileIndices = rest.fileIndices.indexOf(matchingExTab);
                      if (idxInFileIndices !== -1 && !usedExIndices.has(idxInFileIndices)) {
                        matchedIndex = idxInFileIndices;
                      }
                    }
                  }

                  let existingTab = undefined;
                  if (matchedIndex !== -1 && rest?.fileIndices) {
                    existingTab = rest.fileIndices[matchedIndex];
                    usedExIndices.add(matchedIndex);
                  }

                  // Preserve FileIds globally across all existing files in the process to prevent duplicates
                  const allExistingFiles = rest?.fileIndices?.flatMap((fi: any) => fi.FileDetail || []) || [];
                  const finalFileDetail = Array.isArray(e.FileDetail)
                    ? e.FileDetail.map((fd: any) => {
                      // New Common files must stay FileId 0; matching by same name/path can reuse an old image id.
                      if (fd?.IsNew || Number(fd?.FileId || 0) <= 0) {
                        return { ...fd, FileId: 0 };
                      }

                      const matchedFile = allExistingFiles.find(
                        (ef: any) => {
                          const fileId = Number(ef.FileId || 0);
                          if (fileId > 0 && usedFileIds.has(fileId)) return false;
                          return (
                            (fd.FilePath && ef.FilePath && fd.FilePath === ef.FilePath) ||
                            (fd.S3FilePath && ef.S3FilePath && fd.S3FilePath === ef.S3FilePath)
                          );
                        }
                      );
                      const matchedFileId = Number(matchedFile?.FileId || 0);
                      if (matchedFileId > 0) {
                        usedFileIds.add(matchedFileId);
                      }
                      const preservedFileId =
                        matchedFileId > 0
                          ? matchedFileId
                          : 0;
                      return { ...fd, FileId: preservedFileId };
                    })
                    : [];

                  return {
                    ...e,
                    IsDefault: true,
                    FileDetail: [...finalFileDetail],
                    IsDimension: false,
                    // Never copy common check items from another process.
                    // Keep checklist only if this process already has a matching common tab.
                    CheckItems: Array.isArray(existingTab?.CheckItems)
                      ? [...existingTab.CheckItems]
                      : [],
                  };
                }),
              ...rest.fileIndices
                .filter((e) => e?.IsDimension)
                .map((e) => ({
                  ...e,
                  IsDefault: false,
                  FileDetail: Array.isArray(e.FileDetail)
                    ? [...e.FileDetail]
                    : [],
                  CheckItems: Array.isArray(e.CheckItems)
                    ? [...e.CheckItems]
                    : [],
                })),
              ...rest.fileIndices
                .filter((e) => !e?.IsDimension && !e?.IsDefault)
                .map((e) => ({
                  ...e,
                  IsDefault: false,
                  FileDetail: Array.isArray(e.FileDetail)
                    ? [...e.FileDetail]
                    : [],
                  CheckItems: Array.isArray(e.CheckItems)
                    ? [...e.CheckItems]
                    : [],
                })),
            ]
            : (rest.ProcessId === -3 || rest.ProcessId === -4
              ? [
                ...commonProcess.map((e) => ({
                  ...e,
                  IsDefault: true,
                  FileDetail: Array.isArray(e.FileDetail)
                    ? [...e.FileDetail]
                    : [],
                  IsDimension: false,
                  CheckItems: Array.isArray(e.CheckItems)
                    ? [...e.CheckItems]
                    : [],
                })),
              ]
              : []);
        return {
          ...rest,
          fileIndices: finalIndices.sort(
            (e1, e2) => e2.IsDimension - e1.IsDimension
          ),
        };
      }),
    };


    // console.log('[STEP3 SUBMIT] After Smart Merge - partialData.Process:', partialData.Process?.map((p: any) => ({
    //   ProcessId: p.ProcessId,
    //   fileIndices: p.fileIndices?.filter((f: any) => f.IsDefault).map((f: any) => ({
    //     TabIndex: f.TabIndex,
    //     FileIds: f.FileDetail?.map((fd: any) => fd.FileId)
    //   }))
    // })));

    let finalData = {
      ...partialData,
      Process: partialData.Process?.map(({ ...rest }) => {
        const normalizedDimensionDetails = {
          ...(rest?.DimensionDetails || {}),
          SelectedColumns: dedupeSelectedColumns(
            rest?.DimensionDetails?.SelectedColumns || []
          ),
        };

        return {
          ...rest,
          DimensionDetails: normalizedDimensionDetails,
          fileIndices:
            rest.ProcessId == -3
              ? [
                ...rest.fileIndices
                  .map((e) => ({
                    ...e,
                    FileDetail: e.FileDetail ?? [],
                  }))
                  .filter((e) => !e?.IsDimension),
              ]
              : (Number(rest.ProcessId) === -1 || Number(rest.ProcessId) === -2)
                ? []
                : rest.fileIndices.find((e) => e?.IsDimension)?.IsDimension
                  ? [
                    ...rest.fileIndices
                      .map((e) => ({
                        ...e,
                        FileDetail: e.FileDetail ?? [],
                      }))
                      .sort((a, b) => b.IsDimension - a.IsDimension),
                  ]
                  : [
                    { ...dimensionTable, CheckItems: [], FileDetail: [], DimensionsData: [], ProductTeplars: [] },
                    ...rest.fileIndices
                      .map((e) => ({
                        ...e,
                        FileDetail: e.FileDetail ?? [],
                        CheckItems: [...(e.CheckItems ?? [])],
                      }))
                      .filter((e) => !e?.IsDimension),
                  ],
        };
      }).map(({ ...rest }) => {
        return {
          ...rest,
          fileIndices: rest.fileIndices
            ?.sort((a, b) => b.IsDimension - a.IsDimension)
            ?.map((e, indx) => {
              return { ...e, OriginalTabIndex: e.TabIndex, TabIndex: indx + 1 };
            }),
        };
      }),
    };
    let stepFourData = {
      ...finalData,
      Process: finalData.Process?.map(({ ProcessId, ...rest }) => {
        // Always align by ProcessId (never by array index), otherwise template
        // changes/re-order can copy one process check items into others.
        const processFiles =
          values?.Process?.find(
            (p: any) => Number(p?.ProcessId) === Number(ProcessId)
          )?.fileIndices || [];
        const usedExIndices = new Set<number>();
        return {
          ...rest,
          ProcessId: ProcessId,
          fileIndices: rest.fileIndices
            ?.map((e) => {
              const isTrue = (val: any) => val === true || String(val).toLowerCase() === 'true';
              const eIsCommon = isTrue(e?.IsDefault) || isTrue(e?.isDefault);
              const eIsDimension = isTrue(e?.IsDimension) || isTrue(e?.isDimension);

              // Match common tabs by heading first. File identity is not unique enough
              // after copy/rename flows because different C-tabs can reuse an image.
              let matchedIndex = processFiles?.findIndex((f: any, idx: number) => {
                if (usedExIndices.has(idx)) return false;

                if (eIsCommon && (isTrue(f?.IsDefault) || isTrue(f?.isDefault))) {
                  const ffHeading = String(f?.FileDetail?.[0]?.Heading || '').trim().toLowerCase();
                  const efHeading = String(e?.FileDetail?.[0]?.Heading || '').trim().toLowerCase();
                  return Boolean(ffHeading && efHeading && ffHeading === efHeading);
                }
                if (eIsDimension && (isTrue(f?.IsDimension) || isTrue(f?.isDimension))) {
                  return true;
                }
                if (!isTrue(f?.IsDefault) && !isTrue(f?.IsDimension) && !eIsCommon && !eIsDimension) {
                  return f?.TabIndex === e?.OriginalTabIndex;
                }
                return false;
              }) ?? -1;

              if (matchedIndex === -1) {
                matchedIndex = processFiles?.findIndex((f: any, idx: number) => {
                  if (usedExIndices.has(idx)) return false;

                  if (eIsCommon && (isTrue(f?.IsDefault) || isTrue(f?.isDefault))) {
                    const hasMatchingFile = f?.FileDetail?.some((ff: any) => 
                      e?.FileDetail?.some((ef: any) => {
                        const ffId = Number(ff.FileId || 0);
                        const efId = Number(ef.FileId || 0);
                        if (ffId > 0 && efId > 0) return ffId === efId;

                        const ffPath = ff.FilePath || ff.S3FilePath;
                        const efPath = ef.FilePath || ef.S3FilePath;
                        if (ffPath && efPath) return ffPath === efPath;

                        return false;
                      })
                    );
                    return hasMatchingFile;
                  }
                  return false;
                }) ?? -1;
              }

              if (matchedIndex === -1) {
                const commonIdx = rest.fileIndices.filter(x => x.IsDefault).indexOf(e);
                const existingCommonTabs = processFiles.filter((ex: any) => ex.IsDefault);
                const matchingExTab = existingCommonTabs[commonIdx];
                const eHeading = String(e?.FileDetail?.[0]?.Heading || '').trim();
                const matchingHeading = String(matchingExTab?.FileDetail?.[0]?.Heading || '').trim();
                if (matchingExTab && !eHeading && !matchingHeading) {
                  const idxInProcessFiles = processFiles.indexOf(matchingExTab);
                  if (idxInProcessFiles !== -1 && !usedExIndices.has(idxInProcessFiles)) {
                    matchedIndex = idxInProcessFiles;
                  }
                }
              }

              let isMatching = undefined;
              if (matchedIndex !== -1 && processFiles) {
                isMatching = processFiles[matchedIndex];
                if (!eIsDimension) {
                  usedExIndices.add(matchedIndex);
                }
              }

              if (isMatching) {
                return {
                  ...e,
                  CheckItems: Array.isArray(isMatching.CheckItems) ? [...isMatching.CheckItems] : [],
                  // Trust e.FileDetail from Mapping 1 as it has correct FileIds (Existing or 0)
                  FileDetail: e?.FileDetail || [],
                };
              }

              // If no match in Formik state, just return e (which is already correctly merged in Mapping 1)
              return e;
            })
            .filter((e) => e !== null) as any[],
        };
      }),
    };

    // Remove phantom/empty image tabs (e.g. from template/process reshuffle) so deleted tabs don't reappear.
    const sanitizedStepFourData = {
      ...stepFourData,
      Process: (stepFourData.Process || []).map((process: any) => {
        const cleaned = (process?.fileIndices || []).filter((fi: any) => {
          const isDimension =
            fi?.IsDimension === true || String(fi?.IsDimension).toLowerCase() === 'true';
          if (isDimension) return true;
          return hasValidFileDetails(fi?.FileDetail || []);
        });

        return {
          ...process,
          fileIndices: cleaned.map((fi: any, idx: number) => ({
            ...fi,
            OriginalTabIndex: fi?.OriginalTabIndex ?? fi?.TabIndex,
            TabIndex: idx + 1,
          })),
        };
      }),
    };

    const stepThreeProcessTabsDebug =
      sanitizedStepFourData?.Process?.map((process: any) => ({
        ProcessId: process?.ProcessId,
        ProcessName: process?.ProcessName,
        SelectedColumns: process?.DimensionDetails?.SelectedColumns || [],
        TableData: process?.DimensionDetails?.TableData || [],
        Tabs:
          process?.fileIndices?.map((fi: any) => ({
            TabIndex: fi?.TabIndex,
            IsDefault: fi?.IsDefault,
            IsDimension: fi?.IsDimension,
            Heading: fi?.FileDetail?.[0]?.Heading || '',
            CheckItems: fi?.CheckItems || [],
            DimensionsData: fi?.DimensionsData || [],
          })) || [],
      })) || [];
    // console.log(
    //   'Step 3 -> Step 4 data by process tab (on Next click):',
    //   stepThreeProcessTabsDebug
    // );

    const commonSummary = sanitizedStepFourData.Process?.map((p: any) => ({
      ProcessName: p.ProcessName,
      CommonImages: p.fileIndices
        ?.filter((fi: any) => fi.IsDefault || fi.isDefault)
        .map((fi: any) => ({
          Heading: fi.FileDetail?.[0]?.Heading,
          FileId: fi.FileDetail?.[0]?.FileId
        })) || []
    }));
    // console.log('--- COMMON IMAGE DISTRIBUTION (Step 3 to 4) ---', commonSummary);
    // console.log('Step 3 -> Step 4 full payload (on Next click):', stepFourData);

    // Debug duplicate dimension headers (ex: "IND test") when moving Step 3 -> Step 4.
    const duplicateLabel = 'IND test';
    const dimensionDuplicateDebug =
      stepFourData?.Process?.map((process: any) => {
        const selectedColumns = process?.DimensionDetails?.SelectedColumns || [];
        const tableData = process?.DimensionDetails?.TableData || [];

        const selectedLabelMatches = selectedColumns
          .map((col: any) => ({
            id: col?.id ?? col?.value ?? col?.data?.Id,
            label: col?.label || col?.data?.Messages || '',
          }))
          .filter(
            (col: any) =>
              String(col?.label || '').trim().toLowerCase() ===
              duplicateLabel.toLowerCase()
          );

        const tableLabelMatches = tableData
          .map((row: any) => ({
            dimensionId: row?.DimensionId,
            dimensionColumn: row?.DimensionColumn || '',
          }))
          .filter(
            (row: any) =>
              String(row?.dimensionColumn || '').trim().toLowerCase() ===
              duplicateLabel.toLowerCase()
          );

        return {
          ProcessId: process?.ProcessId,
          ProcessName: process?.ProcessName,
          SelectedColumnsCountForLabel: selectedLabelMatches.length,
          SelectedColumnsForLabel: selectedLabelMatches,
          TableDataCountForLabel: tableLabelMatches.length,
          TableDataForLabel: tableLabelMatches,
        };
      }) || [];

    // console.log(
    //   `Step 3 -> Step 4 duplicate check for "${duplicateLabel}" by process:`,
    //   dimensionDuplicateDebug
    // );

    props.next(sanitizedStepFourData);

    // if (props.id < 0) {
    //   GetTeplarByProductIdService(
    //     Number(getTenantAccessFromLocalStorage()),
    //     values.ProductName
    //   );
    // }
    // } else {
    //   let tempValues = {
    //     ...values,
    //   };
    //   props.next(
    //     {
    //       ...tempValues,
    //       Process: selectedProcesses.map(({ Id, ...rest }) => rest),
    //       IsPrint: loadStepThree,
    //     },
    //     true
    //   );
    // }
    // props.setCloseModal(true);
  };

  return (
    <>
      {
        (getDimensionByProcessIdState.inProgress || productStore.getAllProductIdState.inProgress) &&
        <FormLoader loading={(getDimensionByProcessIdState.inProgress || productStore.getAllProductIdState.inProgress)} />
      }
      <Formik
        initialValues={normalizedInitialValues}
        onSubmit={handleSubmit}
        validateOnBlur={false}
        enableReinitialize
      >
        {({
          values,
          isValid,
          handleSubmit,
          errors,
          setFieldValue,
          setErrors,
          resetForm,
        }) => (
          <Form
            id="formBody"
            onSubmit={handleSubmit}
          >
            <DimensionOrderFetcher />
            
            <Modal.Body>
              <ProductStepper currentStep={2} />
              <div className="row g-0">
                <div className="col-12">
                  <div className="row px-4 mb-2">
                    <div className="col-6 d-flex  align-items-center align-items-xxl-start flex-xxl-column gap-2">
                      <label className="text-nowrap">
                        <FormattedMessage id="product_id" />
                      </label>
                      <input
                        type="text"
                        value={props.initalValues.ProductName}
                        placeholder="-"
                        className="form-control"
                        disabled
                      />
                    </div>
                    <div className="col-6 d-flex align-items-center align-items-xxl-start flex-xxl-column gap-2">
                      <label className="text-nowrap">
                        <FormattedMessage id="katashiki" />
                      </label>
                      <input
                        type="text"
                        value={props.initalValues.Katashiki}
                        placeholder="-"
                        className="form-control"
                        disabled
                      />
                    </div>
                  </div>
                </div>
                <div className="bg-product-master step-two-tab-content">
                  <CommonProcessTabView
                    values={values}
                    setSelectedProcessId={setSelectedProcessId}
                    selectedProcessId={selectedProcessId}
                    id={props.id}
                  />
                  <CommonProcessTabContent
                    id={props.id}
                    selectedProcessId={selectedProcessId}
                    setSelectedProcessId={setSelectedProcessId}
                  />
                </div>
              </div>
            </Modal.Body>
            <Modal.Footer>
              <div className="d-flex justify-content-center">
                <div className="row tab-action g-0">
                  <div className="col-12">
                    <Button
                      data-testid="back"
                      variant="light-danger"
                      onClick={() => {
                        props.prev(values);
                        // if (props.id > 0) {
                        //   if (isValid) props.prev(values);
                        // } else {
                        //   props.prev(values);
                        // }
                      }}
                      disabled={props.isLoading}
                    >
                      <FormattedMessage id="back" />
                    </Button>
                    {
                      <>
                        <LoaderButton
                          type="submit"
                          id="SaveTenant"
                          isLoading={props.isLoading}
                          text="next"
                          onClick={() => { }}
                        />
                        {/* <LoaderButton
                          type="submit"
                          id="SaveTenant"
                          isLoading={props.isLoading}
                          text={props.id > 0 ? 'update' : 'save'}
                        /> */}

                        {/* {props.id < 0 && (
                          <LoaderButton
                            className="btn btn-secondary"
                            type="button"
                            id="SaveTenant"
                            onClick={(e) => {
                              if (selectedProcesses.length === 0) {
                                setErrors({
                                  selectedProcesses: formatMessage(
                                    'select_a_template_or_process'
                                  ),
                                });
                                return;
                              }
                              if (!validateDueDates(selectedProcesses)) {
                                setErrors({
                                  selectedProcesses: formatMessage(
                                    'due_dates_should_be_in_decreasing_order'
                                  ),
                                });
                                return;
                              }
                              handleSubmit1(props.initalValues, false);
                              resetForm();
                              props.setCurrentStep(0);
                            }}
                            isLoading={props.isLoading}
                            text={props.id > 0 ? 'update' : 'save_create_new'}
                          />
                        )} */}
                      </>
                    }
                  </div>
                </div>
              </div>
            </Modal.Footer>
          </Form>
        )}
      </Formik>
    </>
  );
};

export default observer(StepThree);
